import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import projects from '../../data/projects';
import {
  Page,
  BackButton,
  DetailHero,
  HeroBadge,
  HeroTitle,
  HeroTagline,
  OverviewSection,
  OverviewGrid,
  OverviewCard,
  OverviewLabel,
  OverviewText,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  AiSection,
  AiGrid,
  AiCard,
  AiCardTitle,
  AiCardDetail,
  AiTechHint,
  IntegrationsList,
  IntegrationCard,
  IntCategory,
  IntName,
  IntDescription,
  IntFree,
  IntFreeLabel,
  IntUseCase,
  IntUseCaseLabel,
  IntSignup,
  IntLinksRow,
  IntImportant,
  RequirementSection,
  ReqGroup,
  ReqGroupTitle,
  ReqList,
  ReqItem,
  EdgeGrid,
  EdgeCard,
  EdgeNumber,
  EdgeText,
} from './styles';

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = projects.find((p) => p.slug === slug);

  if (!project) {
    return (
      <Page>
        <BackButton onClick={() => navigate('/')}>Back to Projects</BackButton>
        <div style={{ textAlign: 'center', padding: '100px 40px' }}>
          <h2 style={{ color: '#f3f4f6' }}>Project not found</h2>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <BackButton onClick={() => navigate('/')}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M12 15L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Projects
      </BackButton>

      <DetailHero accentColor={project.color} glowColor={project.colorGlow}>
        <HeroBadge accentColor={project.color}>{project.subtitle}</HeroBadge>
        <HeroTitle>{project.title}</HeroTitle>
        <HeroTagline>{project.tagline}</HeroTagline>
      </DetailHero>

      <OverviewSection>
        <SectionHeader>
          <SectionTitle>Overview</SectionTitle>
        </SectionHeader>
        <OverviewGrid>
          <OverviewCard>
            <OverviewLabel>What You're Building</OverviewLabel>
            <OverviewText>{project.overview.description}</OverviewText>
          </OverviewCard>
          <OverviewCard>
            <OverviewLabel>The Core Problem</OverviewLabel>
            <OverviewText>{project.overview.problem}</OverviewText>
          </OverviewCard>
          <OverviewCard full>
            <OverviewLabel>Expected Outcome (After 1 Week)</OverviewLabel>
            <OverviewText>{project.overview.outcome}</OverviewText>
          </OverviewCard>
        </OverviewGrid>
      </OverviewSection>

      <AiSection>
        <SectionHeader>
          <SectionTitle>{project.aiFeatures.title}</SectionTitle>
          <SectionDescription>{project.aiFeatures.description}</SectionDescription>
        </SectionHeader>
        <AiGrid>
          {project.aiFeatures.features.map((feature, i) => (
            <AiCard key={i} accentColor={project.color}>
              <AiCardTitle accentColor={project.color}>
                <span>{String(i + 1).padStart(2, '0')}</span>
                {feature.name}
              </AiCardTitle>
              <AiCardDetail>{feature.detail}</AiCardDetail>
              <AiTechHint accentColor={project.color}>{feature.techHint}</AiTechHint>
            </AiCard>
          ))}
        </AiGrid>
      </AiSection>

      <section style={{ padding: '80px 40px', maxWidth: '1200px', margin: '0 auto' }}>
        <SectionHeader>
          <SectionTitle>Free Integrations & APIs</SectionTitle>
          <SectionDescription>
            Every integration listed here has a free tier sufficient for this hackathon.
            No credit card needed to get started.
          </SectionDescription>
        </SectionHeader>
        <IntegrationsList>
          {project.integrations.map((integration, i) => (
            <IntegrationCard key={i}>
              <IntCategory>{integration.category}</IntCategory>
              <IntName>{integration.name}</IntName>
              <IntDescription>{integration.description}</IntDescription>

              <IntFreeLabel>Free Tier</IntFreeLabel>
              <IntFree>{integration.freeDetails}</IntFree>

              <IntUseCaseLabel>How to Use It</IntUseCaseLabel>
              <IntUseCase>{integration.useCase}</IntUseCase>

              {integration.important && (
                <IntImportant>{integration.important}</IntImportant>
              )}

              <IntLinksRow>
                {integration.links.map((link, j) => (
                  <IntSignup
                    key={j}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M4 10L10 4M10 4H5M10 4V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </IntSignup>
                ))}
              </IntLinksRow>
            </IntegrationCard>
          ))}
        </IntegrationsList>
      </section>

      <RequirementSection>
        <SectionHeader>
          <SectionTitle>Detailed Requirements</SectionTitle>
          <SectionDescription>
            What we expect to see in your submission. Not everything is mandatory, but the more you cover, the stronger your application.
          </SectionDescription>
        </SectionHeader>
        {project.requirements.map((group, i) => (
          <ReqGroup key={i}>
            <ReqGroupTitle>
              <span>{String(i + 1).padStart(2, '0')}</span>
              {group.title}
            </ReqGroupTitle>
            <ReqList>
              {group.items.map((item, j) => (
                <ReqItem key={j}>{item}</ReqItem>
              ))}
            </ReqList>
          </ReqGroup>
        ))}
      </RequirementSection>

      <section style={{ padding: '80px 40px', maxWidth: '1200px', margin: '0 auto' }}>
        <SectionHeader>
          <SectionTitle>Edge Cases to Consider</SectionTitle>
          <SectionDescription>
            These are the scenarios that separate great engineers from good ones.
            Think about these while building.
          </SectionDescription>
        </SectionHeader>
        <EdgeGrid>
          {project.edgeCases.map((edge, i) => (
            <EdgeCard key={i} accentColor={project.color}>
              <EdgeNumber accentColor={project.color}>{String(i + 1).padStart(2, '0')}</EdgeNumber>
              <EdgeText>{edge}</EdgeText>
            </EdgeCard>
          ))}
        </EdgeGrid>
      </section>

      <div style={{ padding: '40px', textAlign: 'center' }}>
        <BackButton onClick={() => navigate('/')} style={{ display: 'inline-flex' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 15L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to All Projects
        </BackButton>
      </div>
    </Page>
  );
}
